import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, Loader2, Clock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const DesenKritikModal = ({ isOpen, onClose, desenId, desenNo, onSuccess, isPublic = false }) => {
  const [kritikMetni, setKritikMetni] = useState('');
  const [saving, setSaving] = useState(false);
  const [revizeler, setRevizeler] = useState([]);
  const [loadingRevizeler, setLoadingRevizeler] = useState(false);

  // Revizeleri yükle
  const loadRevizeler = useCallback(async () => {
    setLoadingRevizeler(true);
    try {
      const { getRevizelerPublic, getRevizeler } = await import('../../services/desenService');
      const getRevizelerFn = isPublic ? getRevizelerPublic : getRevizeler;
      
      const response = await getRevizelerFn(desenId);
      if (response.success) {
        // Tarih sırasına göre sırala (en yeni en üstte)
        const siraliRevizeler = [...(response.data.revizeler || [])].sort((a, b) => {
          const tarihA = new Date(a.revize_tarihi);
          const tarihB = new Date(b.revize_tarihi);
          return tarihB - tarihA;
        });
        setRevizeler(siraliRevizeler);
      }
    } catch (error) {
      console.error('Revizeler yüklenemedi:', error);
      // Hata olsa bile devam et
    } finally {
      setLoadingRevizeler(false);
    }
  }, [desenId, isPublic]);

  useEffect(() => {
    if (isOpen && desenId) {
      loadRevizeler();
    }
  }, [isOpen, desenId, loadRevizeler]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!kritikMetni.trim()) {
      toast.error('Lütfen kritik metnini girin');
      return;
    }

    setSaving(true);
    try {
      const { addKritikPublic, addKritik } = await import('../../services/desenService');
      const addKritikFn = isPublic ? addKritikPublic : addKritik;
      
      await addKritikFn(desenId, kritikMetni);
      
      toast.success('Kritik başarıyla eklendi');
      setKritikMetni('');
      // Revizeleri yeniden yükle
      await loadRevizeler();
      onSuccess && onSuccess();
      // Modal'ı kapatma, kullanıcı manuel kapatabilir
    } catch (error) {
      console.error('Kritik ekleme hatası:', error);
      toast.error(error.response?.data?.message || 'Kritik eklenirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setKritikMetni('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Kritik Ekle
              </h2>
              {desenNo && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Desen: {desenNo}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={saving}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col">
          <div className="space-y-4 flex-1">
            {/* Geçmiş Kritikler - Her zaman göster */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Geçmiş Kritikler {revizeler.length > 0 && `(${revizeler.length})`}
                </h3>
              </div>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {loadingRevizeler ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 text-gray-400 dark:text-gray-500 animate-spin" />
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Yükleniyor...</span>
                  </div>
                ) : revizeler.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Henüz kritik bulunmuyor</p>
                  </div>
                ) : (
                  revizeler.map((revize, index) => (
                    <div
                      key={revize.id || index}
                      className="border-l-4 border-red-500 pl-3 py-2 bg-white dark:bg-gray-800 rounded-r"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-900 dark:text-white">
                          Kritik #{revizeler.length - index}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {formatDate(revize.revize_tarihi)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {revize.kritik_metni}
                      </p>
                      {revize.onay_tarihi && (
                        <div className="flex items-center gap-1 mt-2">
                          <CheckCircle className="h-3 w-3 text-green-600 dark:text-green-400" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Onaylandı: {formatDate(revize.onay_tarihi)}
                          </span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Yeni Kritik Ekleme */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Yeni Kritik Metni <span className="text-red-500">*</span>
              </label>
              <textarea
                value={kritikMetni}
                onChange={(e) => setKritikMetni(e.target.value)}
                placeholder="Yeni kritik metnini buraya girin..."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                disabled={saving}
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Kritik eklendiğinde desen durumu otomatik olarak "Revize" olacaktır.
              </p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            İptal
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving || !kritikMetni.trim()}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Kaydediliyor...</span>
              </>
            ) : (
              'Kaydet'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesenKritikModal;
